import type { PromoCampaignModel, PromoProductModel } from '@/shared/generated'
import type { SortingState } from '@tanstack/react-table'

import { useState } from 'react'
import { ChevronRightIcon, DownloadIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { usePromoProductsQuery } from '@/entities/promo-campaign'

import {
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import SearchField from '@/shared/components/SearchField'
import { Badge } from '@/shared/components/ui/badge'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { Content, Footer, Header, Layout } from '@/shared/components/ui/layout'
import { Loader } from '@/shared/components/ui/loader'
import { Separator } from '@/shared/components/ui/separator'
import { getMasterTableLocale } from '@/shared/i18n'

import { buildDownloadTableHandler } from '../lib/buildDownloadTableHandler'
import { getColumn } from '../lib/getColumn'

export default function PromoCampaignProducts({
	campaign,
}: {
	campaign: PromoCampaignModel
}) {
	const { t } = useTranslation()

	const [searchText, setSearchText] = useState('')
	const { id, name, date_start, date_end } = campaign
	const promoProductsQuery = usePromoProductsQuery(id!)
	const data = promoProductsQuery.data || []
	const handleDownload = buildDownloadTableHandler(t)
	const filteredData = searchText
		? data?.filter(({ sku }) => sku.includes(searchText))
		: data

	return (
		<Layout className='relative overflow-hidden'>
			<Blanket
				className='absolute flex flex-col items-center justify-center space-y-5 rounded-lg bg-transparent'
				isTinted={promoProductsQuery.isLoading}
			>
				<div className='z-50 rounded-lg bg-white/50 p-5 shadow backdrop-blur-md fade-in dark:bg-black/50'>
					<Loader />
				</div>
			</Blanket>
			<Header className='grow-0 justify-between px-3 pb-2'>
				<SearchField
					count={filteredData?.length}
					onValueChange={setSearchText}
					value={searchText}
					placeholder={t('general_search')}
				/>
			</Header>
			<Content className='h-full overflow-hidden px-3'>
				<div className='border-base flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
					<PromoCampaignProductsTable data={filteredData || []} />
				</div>
			</Content>
			<Footer className='grow-0 px-3 py-1'>
				<div className='flex items-center space-x-2'>
					<Badge className='bg-transparent px-0'>
						{t('products_count_items')}:{' '}
						<span className='font-semibold'>{data?.length || 0}</span>
					</Badge>
					{filteredData?.length != data?.length && (
						<>
							<ChevronRightIcon className='size-4 text-border' />
							{t('products_count_items_filtered')}:{' '}
							<span className='font-semibold'>{filteredData?.length}</span>
						</>
					)}
					<Separator orientation='vertical' className='h-4' />
					<Button
						className='px-1 py-0'
						data-testid='download-table'
						onClick={() =>
							handleDownload({
								rows: promoProductsQuery.data!,
								name: `Promo: ${name} ${date_start} - ${date_end}`,
							})
						}
					>
						<DownloadIcon className='mr-1 text-muted-foreground' />
						{t('app.download_table')}
					</Button>
				</div>
			</Footer>
		</Layout>
	)
}

function PromoCampaignProductsTable({ data }: { data: PromoProductModel[] }) {
	const { t } = useTranslation()

	const [sorting, setSorting] = useState<SortingState>([])
	const columns = getColumn(t)
	const table = useMasterReactTable<PromoProductModel>({
		localization: getMasterTableLocale(),
		data,
		columns,
		state: {
			sorting,
		},
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		getRowId: (row) => row.sku,
		enableColumnPinning: false,
		enableColumnActions: false,
		enableVirtualization: true,
		getRowHeight: () => 32,
		getRowCanExpand: () => true,
		onSortingChange: setSorting,
	})

	return <MasterTable table={table} />
}
